// app/pages/Users.jsx
import React, { useState, useEffect } from 'react';
import { Plus, Search, RefreshCw, QrCode } from 'lucide-react';
import { userService } from '../services/user.service';
import UserTable from '../components/users/UserTable';
import UserModal from '../components/users/UserModal';
import PasswordUpdateModal from '../components/users/PasswordUpdateModal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';
import DriverQRModal from '../components/users/DriverQRModal';
import CustomerQRModal from '../components/users/CustomerQRModal';
import { driverQrService } from '../services/driverQr.service';
import { customerQrService } from '../services/customerQr.service';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { useDialog } from '../contexts/DialogContext';

const Users = () => {
    const { isAdmin } = useRoleAccess();
    const { toast } = useDialog();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteUserId, setDeleteUserId] = useState(null);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
    const [roles, setRoles] = useState([]);
    const [showDriverQr, setShowDriverQr] = useState(false);
    const [qrData, setQrData] = useState(null);
    const [qrLoading, setQrLoading] = useState(false);
    const [qrError, setQrError] = useState('');
    const [showCustomerQr, setShowCustomerQr] = useState(false);
    const [customerQrData, setCustomerQrData] = useState(null);
    const [customerQrLoading, setCustomerQrLoading] = useState(false);
    const [customerQrError, setCustomerQrError] = useState('');

    const openDriverPortalQr = async () => {
        setShowDriverQr(true);
        setQrData(null);
        setQrError('');
        setQrLoading(true);
        try {
            const data = await driverQrService.getPortal();
            setQrData(data);
        } catch (error) {
            console.error('Driver portal QR failed:', error);
            const detail =
                error?.errorData?.detail ||
                error?.message ||
                'Failed to load driver login QR';
            const message =
                typeof detail === 'string'
                    ? detail
                    : Array.isArray(detail)
                      ? detail.map((e) => e.msg || JSON.stringify(e)).join('; ')
                      : 'Failed to load driver login QR';
            setQrError(message);
        } finally {
            setQrLoading(false);
        }
    };

    const closeDriverQr = () => {
        setShowDriverQr(false);
        setQrData(null);
        setQrError('');
        setQrLoading(false);
    };

    const openCustomerPortalQr = async () => {
        setShowCustomerQr(true);
        setCustomerQrData(null);
        setCustomerQrError('');
        setCustomerQrLoading(true);
        try {
            const data = await customerQrService.getPortal();
            setCustomerQrData(data);
        } catch (error) {
            console.error('Customer portal QR failed:', error);
            const detail =
                error?.errorData?.detail ||
                error?.message ||
                'Failed to load customer login QR';
            const message =
                typeof detail === 'string'
                    ? detail
                    : Array.isArray(detail)
                      ? detail.map((e) => e.msg || JSON.stringify(e)).join('; ')
                      : 'Failed to load customer login QR';
            setCustomerQrError(message);
        } finally {
            setCustomerQrLoading(false);
        }
    };

    const closeCustomerQr = () => {
        setShowCustomerQr(false);
        setCustomerQrData(null);
        setCustomerQrError('');
        setCustomerQrLoading(false);
    };

    useEffect(() => {
        fetchUsers();
        fetchRoles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, searchTerm, roleFilter]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const params = {
                page: currentPage,
                limit: 20,
                search: searchTerm,
                role: roleFilter
            };

            const data = await userService.getUsers(params);
            setUsers(data.items);
            setTotalPages(data.pages);
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchRoles = async () => {
        try {
            const data = await userService.getRoles();
            setRoles(data.items || []);
        } catch (error) {
            console.error('Error fetching roles:', error);
        }
    };

    const handleCreate = () => {
        setEditingUser(null);
        setShowModal(true);
    };

    const handleEdit = (user) => {
        setEditingUser(user);
        setShowModal(true);
    };

    const handleDelete = (userId) => {
        setDeleteUserId(userId);
        setShowDeleteConfirm(true);
    };

    const handleUpdatePassword = (user) => {
        setSelectedUserForPassword(user);
        setShowPasswordModal(true);
    };

    const confirmDelete = async () => {
        try {
            await userService.deleteUser(deleteUserId);
            fetchUsers();
            setShowDeleteConfirm(false);
        } catch (error) {
            console.error('Error deleting user:', error);
        }
    };

    const handleSave = async (userData) => {
        try {
            if (editingUser) {
                await userService.updateUser(editingUser.id, userData);
            } else {
                await userService.createUser(userData);
            }
            setShowModal(false);
            fetchUsers();
        } catch (error) {
            console.error('Error saving user:', error);
            throw error;
        }
    };

    const handlePasswordUpdate = async (password) => {
        try {
            await userService.updateUserPassword(selectedUserForPassword.id, password);
            toast.success('Password updated successfully!');
        } catch (error) {
            console.error('Error updating password:', error);
            throw error;
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleRoleFilter = (e) => {
        setRoleFilter(e.target.value);
        setCurrentPage(1);
    };

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-md bxshadowhd p-3 flex justify-between items-center mrgall border">
                <h1 className="text-2xl font-semibold text-gray-900">Users Management</h1>
                <div className="flex items-center gap-2">
                    {isAdmin ? (
                        <>
                            <button
                                type="button"
                                onClick={openCustomerPortalQr}
                                className="flex items-center gap-2 px-4 py-2 border border-indigo-600 text-indigo-700 rounded-md hover:bg-indigo-50 transition-colors"
                            >
                                <QrCode className="w-4 h-4" />
                                Customer Login QR
                            </button>
                            <button
                                type="button"
                                onClick={openDriverPortalQr}
                                className="flex items-center gap-2 px-4 py-2 border border-teal-600 text-teal-700 rounded-md hover:bg-teal-50 transition-colors"
                            >
                                <QrCode className="w-4 h-4" />
                                Driver Login QR
                            </button>
                        </>
                    ) : null}
                    <button
                        onClick={handleCreate}
                        className="flex items-center gap-2 px-4 py-2 bg-scarlet-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        Add User
                    </button>
                </div>
            </div>
            <div className="p-2 flex gap-4">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={handleSearch}
                            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                    </div>
                    <select 
                        value={roleFilter}
                        onChange={handleRoleFilter}
                        className="drop_bform px-4 py-2 outline-none bg-blue-700 text-white rounded-lg focus:ring-2 focus:ring-blue-500 [&>option]:bg-white [&>option]:text-gray-900"
                    >
                        <option value="">All Roles</option>
                        {roles.map(role => (
                            <option key={role.id} value={role.role_code}>
                                {role.role_name}
                            </option>
                        ))}
                    </select>
                    <button
                        onClick={fetchUsers}
                        className="px-4 py-2 bg-white border text-gray-500 rounded-lg"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                </div>
            <div className="bg-white rounded-lg bxshadowhd p-6 mrgall border">
                {loading ? (
                    <LoadingSpinner />
                ) : (
                    <UserTable
                        users={users}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onUpdatePassword={handleUpdatePassword}
                    />
                )}

                {totalPages > 1 && (
                    <div className="p-2 bg-gray-50 mt-6 flex justify-center">
                        <nav className="flex gap-2">
                            <button
                                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <span className="px-3 py-1">
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1 border rounded-md bg-white hover:bg-gray-50 disabled:opacity-50"
                            >
                                Next
                            </button>
                        </nav>
                    </div>
                )}
            </div>

            <UserModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                onSave={handleSave}
                user={editingUser}
                roles={roles}
            />

            <ConfirmDialog
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={confirmDelete}
                title="Delete User"
                message="Are you sure you want to delete this user? This action cannot be undone."
            />

            <PasswordUpdateModal
                isOpen={showPasswordModal}
                onClose={() => setShowPasswordModal(false)}
                onSave={handlePasswordUpdate}
                user={selectedUserForPassword}
            />

            {showDriverQr ? (
                <DriverQRModal
                    qrData={qrData}
                    loading={qrLoading}
                    error={qrError}
                    onClose={closeDriverQr}
                />
            ) : null}

            {showCustomerQr ? (
                <CustomerQRModal
                    qrData={customerQrData}
                    loading={customerQrLoading}
                    error={customerQrError}
                    onClose={closeCustomerQr}
                />
            ) : null}
        </div>
    );
};

export default Users;